import {
  Dialog,
  DialogContent,
  DialogContentProps,
  PaperProps,
  SxProps,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { PropsWithChildren } from "react";
import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../../app/constants";
import { useMemoizedDraggablePaper } from "./useMemoizedDraggablePaper";

// Dragging behavior for dialogs:
// - Dragging is allowed only from the header or the actions container, not from the content area.
// - Dialogs can be dragged from elements with className="drag-handle" (default). Dragging is disabled for elements with className="no-drag". Dragging is also disabled for buttons.
// - When using nested dialogs (dialogs that open one or more additional dialogs), each dialog must have a unique drag-handle. Otherwise, dragging from the topmost dialog will move all open dialogs.
// - The unique drag-handle can be passed as a prop to the DialogComponent.

// Dialog variants and usage:
// Card variant:
// - Can be used with the DialogHeader component (formerly ModalHeader) and components like FormContainer, with a gray border around the form card.

// Form variant:
// - Has a white background.
// - Used with the DialogContentWrapper component to provide correct layout and action buttons. Rendering action buttons can be disabled via props, allowing the use of custom form buttons.
// - Using DialogContentWrapper disables dragging for the dialog content area.

export type DialogComponentProps = {
  open: boolean;
  onClose?: () => void;
  autoWidth?: boolean;
  backgroundColor?: string;
  disableBackdropClick?: boolean;
  hideBackdrop?: boolean;
  sx?: SxProps;
  draggable?: boolean;
  DialogContentProps?: DialogContentProps;
  PaperProps?: PaperProps;
  dragHandleSelector?: string;
  TransitionProps?: TransitionProps;
  variant?: "form" | "card";
};

const DialogComponent = ({
  open,
  onClose,
  autoWidth,
  disableBackdropClick = true,
  hideBackdrop = false,
  sx = {},
  draggable = true,
  DialogContentProps = {},
  PaperProps = {},
  dragHandleSelector = DEFAULT_DRAG_HANDLE_SELECTOR,
  TransitionProps = {},
  children,
  variant = "form",
}: PropsWithChildren<DialogComponentProps>) => {
  const widthProps = {
    width: autoWidth ? "auto" : "80%",
    maxWidth: autoWidth ? "unset" : "800px",
  };

  const MemoizedDraggablePaper = useMemoizedDraggablePaper(
    draggable,
    dragHandleSelector,
  );

  const handleClose = (reason: string) => {
    if (!onClose) return;

    if (!disableBackdropClick && reason === "backdropClick") {
      onClose();
    } else if (reason !== "backdropClick") {
      onClose();
    }
  };

  const dialogSx = {
    backgroundColor: variant === "card" ? "#EAEAF1" : "#fff",
    borderRadius: variant === "card" ? "12px" : "8px",
    ...widthProps,
    ...sx,
  };

  const contentSx = {
    display: "flex",
    minHeight: "fit-content",
    maxHeight: "calc(100% - 40px)",
    padding: variant === "card" ? "0px 1em 1em 1em" : 2,
    ...((DialogContentProps as any)?.sx || {}),
  };

  return (
    <Dialog
      open={open}
      PaperComponent={MemoizedDraggablePaper}
      onClose={(_, reason) => handleClose(reason)}
      disableEscapeKeyDown
      hideBackdrop={hideBackdrop}
      disableScrollLock={hideBackdrop}
      sx={hideBackdrop ? { pointerEvents: "none" } : undefined}
      PaperProps={{
        ...PaperProps,
        sx: hideBackdrop
          ? { ...dialogSx, pointerEvents: "auto" }
          : dialogSx,
      }}
      TransitionProps={TransitionProps}
    >
      <DialogContent
        className={dragHandleSelector}
        {...DialogContentProps}
        sx={contentSx}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default DialogComponent;
